import { Box, IconButton, TableCell, styled } from '@mui/material';
import { ChevronDown } from '@styled-icons/bootstrap/ChevronDown';
import { ChevronUp } from '@styled-icons/bootstrap/ChevronUp';
import { CertificateIcon } from 'assets/img/menu';
import React, { FC, Fragment, useRef, useState } from 'react';
import Moment from 'react-moment';
import {
  calculateAttachmentCountOfGroup,
  getCreatedTimestampOfGroup,
} from 'FeatureModules/ImpactClaim/utils/impactClaim.utils';
import { isSafariBrowser } from 'utils/browser.utils';
import InnerTableRow from './InnerTableRow';
import { Date, StyledCell, TableRow } from './styles';
import { IImpactClaimGroup } from './ImpactClaimTableBody';
import { TableAction } from './ImpactClaimTable';
import ClaimTitleAndStatus from '../ClaimTitleAndStatus';

interface ImpactGroupTableRowProps {
  impactClaimGroup: IImpactClaimGroup;
  actions?: TableAction[];
  onSelect: (id: string) => void;
}

const ChevronIconButton = styled(IconButton)(({ theme }) => ({
  marginRight: theme.spacing(2),
}));

// Creates a row with 0 height, but has the global table border-spacing applied so it acts as an additional spaceing item.
// Used to have enough spacing after the innerTableRows in the collapsed group within the shadow container.
const SpacingRow = styled('tr')(() => ({
  height: 0,
}));

const ShadowContainer = styled('div')<{
  height: number;
  open: boolean;
  display: boolean;
}>(({ height, open, display, theme }) => ({
  display: display ? 'block' : 'none',
  position: 'absolute',
  background: '#fff',
  top: 0,
  left: 8,
  right: 8,
  bottom: open ? -height : -8,
  zIndex: -1,
  borderRadius: 6,
  boxShadow: theme.custom.shadows[1],
}));

const ImpactGroupTableRow: FC<ImpactGroupTableRowProps> = ({
  impactClaimGroup,
  actions,
  onSelect,
}) => {
  const [open, setOpen] = useState(false);
  const { impactCatalogItemId, impactClaims } = impactClaimGroup;
  const labelId = `impact-claim-table-${impactCatalogItemId}`;
  const createdTimestamp = getCreatedTimestampOfGroup({
    impactClaims,
  });
  const attachmentCountOfGroup = calculateAttachmentCountOfGroup({
    impactClaims,
  });
  const isSafari = isSafariBrowser();
  // use table cell to calculate the height of shadow container when opening impact claim group
  const firstTableCell = useRef<HTMLTableCellElement | null>(null);

  return (
    <Fragment>
      <TableRow
        data-testid="impact-claim-table-item"
        hover
        key={impactCatalogItemId}
        onClick={(event: { stopPropagation: () => void }) => {
          event.stopPropagation();
          setOpen(!open);
        }}
      >
        <StyledCell ref={firstTableCell} id={labelId} scope="row" width={450}>
          <ClaimTitleAndStatus
            impactCatalogItem={impactClaimGroup.impactClaims[0].impactCatalogItem}
            showSeeComments
            claimStatus="GROUP"
            transparent={false}
          />
        </StyledCell>
        <TableCell width={300} id={labelId} scope="row"></TableCell>
        <TableCell width={200} id={labelId} scope="row">
          <Box display="flex">
            <CertificateIcon />
            <Box pl={1}>
              {attachmentCountOfGroup} proof document
              {attachmentCountOfGroup !== 1 ? 's' : ''}
            </Box>
          </Box>
        </TableCell>
        <TableCell id={labelId} scope="row">
          <Date variant="subtitle2">
            <Moment format="LL">{createdTimestamp}</Moment>
          </Date>
        </TableCell>
        <TableCell width={50} id={labelId} scope="row" align="right">
          <ChevronIconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
            {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </ChevronIconButton>
          <ShadowContainer
            display={!isSafari}
            open={open}
            height={impactClaims.length * ((firstTableCell.current?.clientHeight ?? 0) + 12)}
          />
        </TableCell>
      </TableRow>
      {impactClaims.map((impactClaim, index) => {
        return (
          <InnerTableRow
            key={index}
            impactClaim={impactClaim}
            actions={actions}
            onSelect={onSelect}
            open={open}
          />
        );
      })}
      <SpacingRow />
    </Fragment>
  );
};

export default ImpactGroupTableRow;
